import {Component, effect, inject} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import './utils/array.extensions';
import {ToastModule} from 'primeng/toast';
import {PrimeNG} from 'primeng/config';
import {TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ConfirmDialog} from './components/confirm-dialog/confirm-dialog';
import {UnsavedChangesDialog} from './components/unsaved-changes-dialog/unsaved-changes-dialog';
import {CurrentUserService} from './services/current-user.service';
import {filter} from 'rxjs';

const PRIMENG_LOCALES: Record<string, Partial<typeof PrimeNG.prototype.translation>> = {
  'fr-BE': {
    firstDayOfWeek: 1,
    dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
    monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    today: "Aujourd'hui",
    clear: 'Effacer',
  },
  'nl-BE': {
    firstDayOfWeek: 1,
    dayNames: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
    dayNamesShort: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
    dayNamesMin: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
    monthNames: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
    today: 'Vandaag',
    clear: 'Wissen',
  },
  'en-UK': {
    firstDayOfWeek: 1,
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
  },
};

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule, ConfirmDialog, UnsavedChangesDialog],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly primeNGConfig = inject(PrimeNG);
  private readonly translateService = inject(TranslateService);
  private readonly currentUserService = inject(CurrentUserService);

  private readonly langChange = toSignal(this.translateService.onLangChange, {
    initialValue: {lang: this.translateService.getCurrentLang() ?? 'fr-BE', translations: {}},
  });

  constructor() {
    const initialLang = localStorage.getItem('lang') ?? 'fr-BE';
    this.primeNGConfig.setTranslation(PRIMENG_LOCALES[initialLang] ?? PRIMENG_LOCALES['fr-BE']);

    effect(() => {
      const lang = this.langChange().lang;
      this.primeNGConfig.setTranslation(PRIMENG_LOCALES[lang] ?? PRIMENG_LOCALES['fr-BE']);
    });

    toObservable(this.currentUserService.currentUser).pipe(
      filter(user => !!user?.localeIsoCode),
      takeUntilDestroyed(),
    ).subscribe(user => {
      localStorage.setItem('lang', user!.localeIsoCode!);
      this.translateService.use(user!.localeIsoCode!);
    });
  }
}
